import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import {
  useGetUpstreamSyncInfo,
  useUpdateUpstreamSyncSettings,
  useSyncRemoteModel,
} from "../ModelService";

interface RemoteModelSyncSettingsProps {
  modelId: number;
}

const RemoteModelSyncSettings: React.FC<RemoteModelSyncSettingsProps> = ({
  modelId,
}) => {
  const {
    data: syncInfo,
    isLoading,
    error,
    refetch,
  } = useGetUpstreamSyncInfo(modelId);
  const updateSettings = useUpdateUpstreamSyncSettings();
  const syncModel = useSyncRemoteModel();

  const handleToggleSyncEnabled = async () => {
    if (syncInfo) {
      await updateSettings.mutateAsync({
        modelId,
        syncEnabled: !syncInfo.sync_enabled,
      });
    }
  };

  const handleSyncNow = async () => {
    await syncModel.mutateAsync(modelId);
    refetch();
  };

  const formatLastSync = (timestamp: string | null) => {
    if (!timestamp) return "Never";
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return "Unknown";
    }
  };

  const renderSyncStatus = () => {
    if (!syncInfo) return null;

    const status = syncInfo.last_upstream_sync_status;
    const syncTime = formatLastSync(syncInfo.last_upstream_sync_at);

    if (!status) {
      return (
        <Tooltip title="Never synced">
          <Chip
            icon={<PendingIcon />}
            label="Never synced"
            size="small"
            color="default"
            variant="outlined"
          />
        </Tooltip>
      );
    }

    if (status === "success") {
      return (
        <Tooltip title={`Last synced ${syncTime}`}>
          <Chip
            icon={<SuccessIcon />}
            label={`Synced ${syncTime}`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={syncInfo.last_upstream_sync_error || "Sync failed"}>
        <Chip
          icon={<ErrorIcon />}
          label="Sync failed"
          size="small"
          color="error"
          variant="outlined"
        />
      </Tooltip>
    );
  };

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load sync settings
      </Alert>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2 }}
      data-testid="remote-model-sync-settings"
    >
      <CardHeader
        title={
          <Typography variant="subtitle1" fontWeight={600}>
            Upstream Sync Settings
          </Typography>
        }
        subheader="Configure how this model syncs capabilities from the remote source"
      />
      <CardContent>
        <Stack spacing={2}>
          {/* Scheduled sync toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Enable scheduled sync
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Include this model in periodic capability sync from upstream
              </Typography>
            </Box>
            <Switch
              checked={syncInfo?.sync_enabled || false}
              onChange={handleToggleSyncEnabled}
              disabled={updateSettings.isLoading}
              data-testid="sync-enabled-toggle"
              data-attr="models:toggle:sync-enabled"
            />
          </Box>

          {/* Sync status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Last sync status
              </Typography>
              {syncInfo?.last_upstream_sync_error && (
                <Typography variant="caption" color="error.main">
                  {syncInfo.last_upstream_sync_error}
                </Typography>
              )}
            </Box>
            {renderSyncStatus()}
          </Box>

          {/* Sync now button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Sync capabilities from upstream now">
              <span>
                <IconButton
                  onClick={handleSyncNow}
                  disabled={syncModel.isLoading}
                  color="primary"
                  data-testid="sync-now-button"
                  data-attr="models:btn:sync-now"
                >
                  {syncModel.isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RemoteModelSyncSettings;
